import { Injectable } from '@angular/core';
import jsPDF from 'jspdf';

import { ExternalEoiRetailPdfData } from './external-eoi.model';

const MARGIN = 40;
const DARK = '#1c2b39';
const GRAY_HEADER = '#d4d6d7';
const LABEL_BG = '#f0f1f1';
const BORDER = '#a9adb0';
const TEXT = '#1a2f45';
const DEFAULT_PROJECT_NAME = 'Project Name';
/**
 * The one label-column width every row on every page shares, so a single vertical line runs down
 * the whole document — a plain field row's own explicit `labelWidth`, a tall row's, a checkbox row's,
 * and the first box of a dual-box row (Phone Number, CVV Number, BSB) all resolve to this same
 * fraction of the content width rather than each picking their own.
 */
const LABEL_COL_RATIO = 0.32;
/** Big enough for the custom checkmark appearance (`checkedCheckboxAppearance`) to render legibly. */
const CHECKBOX_SIZE = 10;

/** One box across a row: a label cell plus its value, as a fraction of the row's total width. */
interface RetailPdfFieldSpec {
  label: string;
  value: string;
  widthRatio: number;
  /** Overrides the default 42%-of-this-box label width — used to line up narrower paired boxes (Phone/DOB, CVV/Expiry). */
  labelWidth?: number;
}

/**
 * Renders an `ExternalEoiRetailPdfData` as a pixel-faithful copy of the client's "EOI & Deposit
 * Authority Fillable - RETAIL 2025.pdf" (originally built for Queen's Wharf Tower) — same three
 * pages, same section bars, boxes and checkboxes, in the same order. Every box and checkbox is a
 * real AcroForm field (`TextField`/`CheckBox`, via jsPDF's bundled AcroForm plugin) pre-filled with
 * whatever answer exists, so a recipient can open the PDF in any reader and type into or tick
 * whatever's still blank — a flat, uneditable rendering would not be "fillable" the way the source
 * document is. Every box is fillable, including the trust account's own fixed particulars (Name/
 * Bank/BSB/Account Number/Swift Code) — pre-filled with the same value for every project, but still
 * a real editable field rather than locked text, matching the source document's own behaviour. The
 * masthead/footer/bank reference show the current project's own name; the seller entity, Avanti
 * Project Marketing and HWL Ebsworth wording stay fixed for every project (see
 * `buildExternalEoiRetailPdfData`'s doc comment). This service draws it but owns no EOI domain
 * knowledge itself, so it stays a `providedIn: 'root'` renderer any "Download PDF" button can call.
 *
 * Card number/CVV/expiry and both signatures are also real, blank, editable fields (so a viewer can
 * fill them by hand in a PDF reader like the source form allows) but are never pre-filled by this
 * service: card details are a PCI-DSS risk to capture and store digitally in Remmi itself, and a
 * signature cannot be produced from a typed answer.
 */
@Injectable({ providedIn: 'root' })
export class ExternalEoiPdfService {
  /** Reset per `build()` call — every AcroForm field needs a document-unique name. */
  private fieldSeq = 0;

  build(data: ExternalEoiRetailPdfData): jsPDF {
    this.fieldSeq = 0;
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    this.drawPage1(doc, data, pageWidth);
    this.drawFooter(doc, data, pageWidth, pageHeight);

    doc.addPage();
    this.drawPage2(doc, data, pageWidth);
    this.drawFooter(doc, data, pageWidth, pageHeight);

    doc.addPage();
    this.drawPage3(doc, data, pageWidth);
    this.drawFooter(doc, data, pageWidth, pageHeight);

    this.enableLiveFieldAppearances(doc);
    return doc;
  }

  /**
   * jsPDF bakes each field's appearance in at generation time and never asks the PDF viewer to
   * redraw it — so a viewer that repaints form widgets straight from its own default appearance
   * stream (rather than regenerating one from whatever the person actually typed) can let someone
   * click into a field, place a cursor, and still show nothing as they type. Setting `/NeedAppearances
   * true` on the AcroForm dictionary is the standard fix: it tells every compliant viewer to
   * regenerate each field's appearance from its live value instead of trusting the one shipped in the
   * PDF, which is exactly what's needed once a person starts editing a field after opening it.
   *
   * `NeedAppearances` isn't a property jsPDF's `AcroFormDictionary` class defines, so it can't just be
   * assigned — its own serialiser (`getKeyValueListForStream`) only ever emits a property that was
   * defined non-configurable, so this defines one the same way the class's own properties are.
   */
  private enableLiveFieldAppearances(doc: jsPDF): void {
    const root = (doc as any).internal?.acroformPlugin?.acroFormDictionaryRoot;
    if (!root || Object.prototype.hasOwnProperty.call(root, 'NeedAppearances')) return;
    Object.defineProperty(root, 'NeedAppearances', { value: true, enumerable: true, configurable: false });
  }

  download(data: ExternalEoiRetailPdfData, fileName?: string): void {
    this.build(data).save(this.resolveFileName(data, fileName));
  }

  private resolveFileName(data: ExternalEoiRetailPdfData, fileName?: string): string {
    const buyer = data.buyer1.surname || data.buyer1.firstMiddleNames || 'Buyer';
    const base = (fileName || `EOI & Deposit Authority - ${buyer}${data.apartmentNumber ? ' - Lot ' + data.apartmentNumber : ''}`).trim();
    const today = new Date().toLocaleDateString('en-GB').split('/').join('-');
    return `${base} - ${today}.pdf`;
  }

  // ---------------------------------------------------------------------
  // Page 1 — Expression of Interest & Particulars to Prepare Contract
  // ---------------------------------------------------------------------

  private drawPage1(doc: jsPDF, data: ExternalEoiRetailPdfData, pageWidth: number): void {
    let y = 40;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(DARK);
    doc.text((data.projectName || DEFAULT_PROJECT_NAME).toUpperCase(), pageWidth / 2, y, { align: 'center' });
    y += 22;

    y = this.drawTitleBar(doc, 'EXPRESSION OF INTEREST & PARTICULARS TO PREPARE CONTRACT', y, pageWidth);
    y += 10;

    const contentWidth = pageWidth - MARGIN * 2;
    const halfW = contentWidth / 2;
    doc.setDrawColor(BORDER);
    doc.setFillColor('#ececec');
    doc.rect(MARGIN, y, halfW - 4, 34, 'F');
    doc.rect(MARGIN, y, halfW - 4, 34, 'S');
    doc.rect(MARGIN + halfW + 4, y, halfW - 4, 34, 'F');
    doc.rect(MARGIN + halfW + 4, y, halfW - 4, 34, 'S');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(TEXT);
    doc.text(
      doc.splitTextToSize(
        'TO: QWB RESIDENTIAL PRECINCT OPERATIONS PTY LTD ACN 608 792 329 AS TRUSTEE FOR THE QWB RESIDENTIAL OPERATIONS TOWER 5 TRUST',
        halfW - 12
      ),
      MARGIN + 6,
      y + 10
    );
    doc.text(
      doc.splitTextToSize(
        'FROM: THE BUYER AS DESCRIBED BELOW TENDERS THIS EXPRESSION OF INTEREST TO BUY THE PROPERTY IN ACCORDANCE WITH THE PARTICULARS SET OUT BELOW',
        halfW - 12
      ),
      MARGIN + halfW + 10,
      y + 10
    );
    y += 40;

    y = this.drawFieldRow(
      doc,
      [{ label: 'Apartment Number', value: data.apartmentNumber, widthRatio: 1, labelWidth: contentWidth * LABEL_COL_RATIO }],
      y,
      pageWidth
    );
    y += 8;

    y = this.drawSectionHeader(doc, 'Buyer #1', y, pageWidth);
    y = this.drawBuyerRows(doc, data.buyer1, y, pageWidth);

    y = this.drawSectionHeader(doc, 'Buyer #2', y, pageWidth);
    y = this.drawBuyerRows(doc, data.buyer2, y, pageWidth);

    // The paper form only ever printed two buyer blocks — this one only appears when the EOI
    // actually has a third purchaser, so the common case still looks exactly like the source form.
    if (this.hasBuyerDetails(data.buyer3)) {
      y = this.drawSectionHeader(doc, 'Buyer #3', y, pageWidth);
      y = this.drawBuyerRows(doc, data.buyer3, y, pageWidth);
    }

    y = this.drawTallFieldRow(doc, 'Full Address', data.fullAddress, y, pageWidth);
    y += 8;

    y = this.drawSectionHeader(doc, 'Company Name', y, pageWidth);
    y = this.drawFieldRow(
      doc,
      [{ label: 'Company Name', value: data.companyName, widthRatio: 1, labelWidth: contentWidth * LABEL_COL_RATIO }],
      y,
      pageWidth
    );
    y = this.drawFieldRow(doc, [{ label: 'ACN', value: data.companyAcn, widthRatio: 1, labelWidth: contentWidth * LABEL_COL_RATIO }], y, pageWidth);
    y = this.drawFieldRow(
      doc,
      [{ label: 'Name of Trust', value: data.trustName, widthRatio: 1, labelWidth: contentWidth * LABEL_COL_RATIO }],
      y,
      pageWidth
    );
    doc.setFillColor('#ececec');
    doc.rect(MARGIN, y, contentWidth, 16, 'F');
    doc.setDrawColor(BORDER);
    doc.rect(MARGIN, y, contentWidth, 16, 'S');
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7.5);
    doc.setTextColor('#555f68');
    doc.text('Please note director/s name and contact details above', MARGIN + 6, y + 11);
    y += 16 + 8;

    y = this.drawCheckboxRow(
      doc,
      'Buyer Purchasing For',
      ['Owner Occupier', 'Investment Purposes'],
      data.purchasingFor ? [data.purchasingFor] : [],
      y,
      pageWidth
    );
    const firbOptions = ['No', `Yes – Nationality: ${data.firbNationality || '_______________'}`];
    const firbChecked = data.firb ? [data.firb === 'Yes' ? firbOptions[1] : 'No'] : [];
    y = this.drawCheckboxRow(doc, 'Foreign Interest (FIRB)', firbOptions, firbChecked, y, pageWidth);
    y += 6;
    y = this.drawCheckboxRow(
      doc,
      'Colour Scheme',
      ['Light (Margaret)', 'Dark (William)', 'Executive Colour Scheme (Penthouse Levels Only)'],
      data.colourScheme ? [data.colourScheme] : [],
      y,
      pageWidth
    );
  }

  /** True once any field on a buyer block has an answer — governs whether the optional Buyer #3 section renders at all. */
  private hasBuyerDetails(buyer: ExternalEoiRetailPdfData['buyer1']): boolean {
    return !!(buyer.firstMiddleNames || buyer.surname || buyer.email || buyer.phone || buyer.dateOfBirth);
  }

  private drawBuyerRows(doc: jsPDF, buyer: ExternalEoiRetailPdfData['buyer1'], y: number, pageWidth: number): number {
    const contentWidth = pageWidth - MARGIN * 2;
    y = this.drawFieldRow(
      doc,
      [{ label: 'First & Middle Names', value: buyer.firstMiddleNames, widthRatio: 1, labelWidth: contentWidth * LABEL_COL_RATIO }],
      y,
      pageWidth
    );
    y = this.drawFieldRow(doc, [{ label: 'Surname', value: buyer.surname, widthRatio: 1, labelWidth: contentWidth * LABEL_COL_RATIO }], y, pageWidth);
    y = this.drawFieldRow(
      doc,
      [{ label: 'Email Address', value: buyer.email, widthRatio: 1, labelWidth: contentWidth * LABEL_COL_RATIO }],
      y,
      pageWidth
    );
    y = this.drawFieldRow(
      doc,
      [
        // `labelWidth` set explicitly (not left to the field's own 42%-of-its-box default) so this
        // row's label divider lines up with every full-width row above/below it, not just with its
        // own narrower 60%-wide box.
        { label: 'Phone Number', value: buyer.phone, widthRatio: 0.6, labelWidth: contentWidth * LABEL_COL_RATIO },
        { label: 'Date of Birth', value: buyer.dateOfBirth, widthRatio: 0.4, labelWidth: contentWidth * 0.4 * 0.5 },
      ],
      y,
      pageWidth
    );
    return y;
  }

  // ---------------------------------------------------------------------
  // Page 2 — Optional Upgrades / Price / Solicitor / Identification
  // ---------------------------------------------------------------------

  private drawPage2(doc: jsPDF, data: ExternalEoiRetailPdfData, pageWidth: number): void {
    const contentWidth = pageWidth - MARGIN * 2;
    let y = 40;

    y = this.drawSectionHeader(doc, 'Optional Upgrades (Inc GST)', y, pageWidth);
    y = this.drawCheckboxRow(doc, 'Finishes', ['Emerald to Ruby', 'Ruby to Diamond'], data.upgrades.finishes, y, pageWidth);
    y = this.drawCheckboxRow(
      doc,
      'Appliances',
      ['Emerald to Ruby', 'Ruby to Gaggenau', 'Ruby to Diamond', 'Diamond to Gaggenau'],
      data.upgrades.appliances,
      y,
      pageWidth
    );
    y = this.drawCheckboxRow(doc, 'Lighting Remote Automation', ['Emerald and Ruby to Diamond'], data.upgrades.lightingAutomation, y, pageWidth);
    y = this.drawCheckboxRow(
      doc,
      'Extra Upgrades',
      ['European Brand Fridge/Freezer', 'Boiling and Chilled Water Tap to Kitchen', 'Pendant Light to Kitchen Option', 'Wine Fridge'],
      data.upgrades.extra,
      y,
      pageWidth
    );
    y += 8;

    y = this.drawSectionHeader(doc, 'Price', y, pageWidth);
    y = this.drawFieldRow(
      doc,
      [{ label: 'Property Price', value: data.propertyPrice, widthRatio: 1, labelWidth: contentWidth * LABEL_COL_RATIO }],
      y,
      pageWidth
    );
    y = this.drawFieldRow(
      doc,
      [{ label: 'Upgrade Price', value: data.upgradePrice, widthRatio: 1, labelWidth: contentWidth * LABEL_COL_RATIO }],
      y,
      pageWidth
    );
    y = this.drawFieldRow(
      doc,
      [{ label: 'Total Price', value: data.totalPrice, widthRatio: 1, labelWidth: contentWidth * LABEL_COL_RATIO }],
      y,
      pageWidth
    );
    y = this.drawCheckboxRow(
      doc,
      'Initial Deposit',
      ['$2,000', '$5,000'],
      data.initialDepositChoice ? [data.initialDepositChoice] : [],
      y,
      pageWidth
    );
    y = this.drawFieldRow(
      doc,
      [{ label: 'Balance Deposit', value: data.balanceDeposit, widthRatio: 1, labelWidth: contentWidth * LABEL_COL_RATIO }],
      y,
      pageWidth
    );
    y = this.drawFieldRow(
      doc,
      [{ label: 'Total Deposit', value: data.totalDeposit, widthRatio: 1, labelWidth: contentWidth * LABEL_COL_RATIO }],
      y,
      pageWidth
    );
    y += 8;

    y = this.drawTallFieldRow(doc, 'Notes', data.notes, y, pageWidth, 30);
    y += 8;

    y = this.drawSectionHeader(doc, "Solicitor's Information", y, pageWidth);
    y = this.drawFieldRow(
      doc,
      [{ label: 'Solicitor Firm Name', value: data.solicitor.firmName, widthRatio: 1, labelWidth: contentWidth * LABEL_COL_RATIO }],
      y,
      pageWidth
    );
    y = this.drawFieldRow(
      doc,
      [{ label: 'Contact Person', value: data.solicitor.contactPerson, widthRatio: 1, labelWidth: contentWidth * LABEL_COL_RATIO }],
      y,
      pageWidth
    );
    y = this.drawFieldRow(
      doc,
      [{ label: 'Phone Number', value: data.solicitor.phone, widthRatio: 1, labelWidth: contentWidth * LABEL_COL_RATIO }],
      y,
      pageWidth
    );
    y = this.drawFieldRow(
      doc,
      [{ label: 'Email Address', value: data.solicitor.email, widthRatio: 1, labelWidth: contentWidth * LABEL_COL_RATIO }],
      y,
      pageWidth
    );
    y = this.drawTallFieldRow(doc, 'Full Address', data.solicitor.address, y, pageWidth);
    y += 8;

    y = this.drawCheckboxRow(
      doc,
      "Salesperson Has Sighted Buyer/s' Identification",
      ['Yes', 'No'],
      data.identificationSighted ? [data.identificationSighted] : [],
      y,
      pageWidth
    );
    y += 8;
    this.drawFieldRow(
      doc,
      [{ label: 'Salesperson', value: data.salesperson, widthRatio: 1, labelWidth: contentWidth * LABEL_COL_RATIO }],
      y,
      pageWidth
    );
  }

  // ---------------------------------------------------------------------
  // Page 3 — Deposit Authority Form
  // ---------------------------------------------------------------------

  private drawPage3(doc: jsPDF, data: ExternalEoiRetailPdfData, pageWidth: number): void {
    const contentWidth = pageWidth - MARGIN * 2;
    let y = 40;

    y = this.drawTitleBar(doc, 'DEPOSIT AUTHORITY FORM', y, pageWidth);
    y += 10;

    const da = data.depositAuthority;
    y = this.drawFieldRow(
      doc,
      [{ label: 'Apartment Number', value: da.apartmentNumber, widthRatio: 1, labelWidth: contentWidth * LABEL_COL_RATIO }],
      y,
      pageWidth
    );
    y = this.drawFieldRow(
      doc,
      [{ label: 'Deposit Amount (AUD$)', value: da.depositAmount, widthRatio: 1, labelWidth: contentWidth * LABEL_COL_RATIO }],
      y,
      pageWidth
    );
    y = this.drawFieldRow(
      doc,
      [{ label: 'I, (Full Name)', value: da.fullName, widthRatio: 1, labelWidth: contentWidth * LABEL_COL_RATIO }],
      y,
      pageWidth
    );
    y = this.drawTallFieldRow(doc, 'Of (Address)', da.address, y, pageWidth);
    y = this.drawFieldRow(doc, [{ label: 'Phone Number', value: da.phone, widthRatio: 1, labelWidth: contentWidth * LABEL_COL_RATIO }], y, pageWidth);
    y = this.drawFieldRow(
      doc,
      [{ label: 'Email Address', value: da.email, widthRatio: 1, labelWidth: contentWidth * LABEL_COL_RATIO }],
      y,
      pageWidth
    );
    y += 10;

    // Pay by credit card — card number/CVV/expiry are deliberately always blank; see this class's doc comment.
    y = this.drawSectionHeader(doc, 'Pay By Credit Card', y, pageWidth);
    y = this.drawCheckboxRow(
      doc,
      '',
      ['Authorise the above amount to be processed on my credit card and held in the trust account of Avanti Project Marketing'],
      [],
      y,
      pageWidth
    );
    y = this.drawCheckboxRow(doc, '', ['I acknowledge card surcharges (non-refundable): 2% local card | 3.8% international card'], [], y, pageWidth);
    y = this.drawCheckboxRow(doc, 'Card Type', ['Visa', 'Mastercard'], [], y, pageWidth);
    y = this.drawFieldRow(doc, [{ label: 'Card Number', value: '', widthRatio: 1, labelWidth: contentWidth * LABEL_COL_RATIO }], y, pageWidth);
    y = this.drawFieldRow(doc, [{ label: 'Name on Card', value: '', widthRatio: 1, labelWidth: contentWidth * LABEL_COL_RATIO }], y, pageWidth);
    y = this.drawFieldRow(
      doc,
      [
        // Matches LABEL_COL_RATIO, used by Card Number/Name on Card above, not this box's own 50%.
        { label: 'CVV Number', value: '', widthRatio: 0.5, labelWidth: contentWidth * LABEL_COL_RATIO },
        { label: 'Expiry Date', value: '', widthRatio: 0.5 },
      ],
      y,
      pageWidth
    );
    y += 6;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(TEXT);
    doc.text('OR', MARGIN, y + 10);
    y += 16;

    y = this.drawSectionHeader(doc, 'Bank Transfer', y, pageWidth);
    y = this.drawCheckboxRow(doc, '', ['Confirm payment of the above amount by EFT, held in the trust account of HWL Ebsworth'], [], y, pageWidth);
    // The trust account's own particulars are still real fillable fields (matching every other box
    // on this form), just pre-filled with the fixed values every project shares.
    y = this.drawFieldRow(
      doc,
      [{ label: 'Name', value: 'HWL Ebsworth Lawyers Law Practice Trust Account', widthRatio: 1, labelWidth: contentWidth * LABEL_COL_RATIO }],
      y,
      pageWidth
    );
    y = this.drawFieldRow(
      doc,
      [{ label: 'Bank', value: 'Westpac Banking Corporation', widthRatio: 1, labelWidth: contentWidth * LABEL_COL_RATIO }],
      y,
      pageWidth
    );
    y = this.drawFieldRow(
      doc,
      [
        // Matches LABEL_COL_RATIO, used by Name/Bank/Swift Code above, not this box's own 50%.
        { label: 'BSB', value: '034 003', widthRatio: 0.5, labelWidth: contentWidth * LABEL_COL_RATIO },
        { label: 'Account Number', value: '24 66 34', widthRatio: 0.5, labelWidth: contentWidth * 0.5 * 0.5 },
      ],
      y,
      pageWidth
    );
    y = this.drawFieldRow(doc, [{ label: 'Swift Code', value: 'WPACAU2S', widthRatio: 1, labelWidth: contentWidth * LABEL_COL_RATIO }], y, pageWidth);
    const buyerName = da.fullName || data.buyer1.surname || data.buyer1.firstMiddleNames || 'Your name';
    const projectName = data.projectName || DEFAULT_PROJECT_NAME;
    y = this.drawTallFieldRow(doc, 'Reference', `${buyerName}: Lot ${da.apartmentNumber || 'number'}: ${projectName}`, y, pageWidth);
    y = this.drawTallFieldRow(doc, 'Please email copy of receipt to', 'lmargetts@hwle.com.au; contracts@qwtower.com.au', y, pageWidth);
    y += 10;

    const legalParagraphs = [
      'This Deposit Authority Form does not constitute any binding commitment between the Buyer and the Seller (QWB Residential Precinct Operations Pty Ltd ACN 608 792 329 as Trustee for the QWB Residential Operations Tower 5 Trust). No contract is formed until a Contract of Sale is executed by both the Buyer and the Seller.',
      'The below signatory authorises Avanti Project Marketing Pty Ltd Trust Account to transfer any deposit monies held to the Seller’s Solicitor in accordance with the terms of the Contract of Sale and withdraw any credit card surcharges incurred as above. Tenders $2,000.00 as an indication of the Buyer’s intent to enter into a Sale Contract to buy the Unit. This amount is to be held in the trust account of Avanti Project Marketing on the Buyer’s behalf and will be refunded to the Buyer in full if the Buyer does not proceed with the purchase (excluding surcharges). Authorises and directs Avanti Project Marketing to hold the amount tendered as Deposit Holder in part payment of the Deposit under the Sale Contract to buy the Unit. Will be required to pay a deposit of 10% of the purchase price within the timeframe noted within the Contract of Sale. Consents to the Seller and its agents giving the Buyer and its agents disclosure, contract and other materials by electronic means.',
    ];
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor('#48596a');
    for (const paragraph of legalParagraphs) {
      const lines = doc.splitTextToSize(paragraph, contentWidth);
      doc.text(lines, MARGIN, y + 8);
      y += lines.length * 9 + 6;
    }
    y += 6;

    y = this.drawFieldRow(doc, [{ label: 'Date', value: '', widthRatio: 1, labelWidth: contentWidth * LABEL_COL_RATIO }], y, pageWidth);
    y = this.drawFieldRow(doc, [{ label: 'Buyer #1 Signature', value: '', widthRatio: 1, labelWidth: contentWidth * LABEL_COL_RATIO }], y, pageWidth);
    this.drawFieldRow(doc, [{ label: 'Buyer #2 Signature', value: '', widthRatio: 1, labelWidth: contentWidth * LABEL_COL_RATIO }], y, pageWidth);
  }

  // ---------------------------------------------------------------------
  // Drawing primitives
  // ---------------------------------------------------------------------

  private drawTitleBar(doc: jsPDF, text: string, y: number, pageWidth: number, height = 24): number {
    doc.setFillColor(DARK);
    doc.rect(MARGIN, y, pageWidth - MARGIN * 2, height, 'F');
    doc.setTextColor('#ffffff');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10.5);
    doc.text(text, pageWidth / 2, y + height / 2 + 3.5, { align: 'center' });
    return y + height;
  }

  private drawSectionHeader(doc: jsPDF, text: string, y: number, pageWidth: number, height = 16): number {
    doc.setFillColor(GRAY_HEADER);
    doc.rect(MARGIN, y, pageWidth - MARGIN * 2, height, 'F');
    doc.setDrawColor(BORDER);
    doc.setLineWidth(0.5);
    doc.rect(MARGIN, y, pageWidth - MARGIN * 2, height, 'S');
    doc.setTextColor(TEXT);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text(text.toUpperCase(), MARGIN + 6, y + height / 2 + 2.5);
    return y + height;
  }

  /** One or more label/value boxes across a row. Grows taller if a label needs to wrap. */
  private drawFieldRow(doc: jsPDF, fields: RetailPdfFieldSpec[], y: number, pageWidth: number, height = 18): number {
    const contentWidth = pageWidth - MARGIN * 2;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    const prepared = fields.map(f => {
      const w = contentWidth * f.widthRatio;
      const labelW = f.labelWidth ?? w * 0.42;
      const labelLines = doc.splitTextToSize(f.label.toUpperCase(), labelW - 10);
      return { ...f, w, labelW, labelLines };
    });
    const rowHeight = Math.max(height, ...prepared.map(f => f.labelLines.length * 10 + 8));

    let x = MARGIN;
    doc.setDrawColor(BORDER);
    doc.setLineWidth(0.5);
    for (const f of prepared) {
      doc.setFillColor(LABEL_BG);
      doc.rect(x, y, f.labelW, rowHeight, 'F');
      doc.rect(x, y, f.labelW, rowHeight, 'S');
      doc.rect(x + f.labelW, y, f.w - f.labelW, rowHeight, 'S');
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor('#555f68');
      doc.text(f.labelLines, x + 5, f.labelLines.length > 1 ? y + 10 : y + rowHeight / 2 + 2.5);
      this.addTextField(doc, f.value || '', x + f.labelW + 3, y + 2, f.w - f.labelW - 6, rowHeight - 4);
      x += f.w;
    }
    return y + rowHeight;
  }

  /** A full-width row whose value cell wraps multi-line text (an address, notes, the reference line). */
  private drawTallFieldRow(doc: jsPDF, label: string, value: string, y: number, pageWidth: number, minHeight = 18): number {
    const contentWidth = pageWidth - MARGIN * 2;
    const labelW = contentWidth * LABEL_COL_RATIO;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    const labelLines = doc.splitTextToSize(label.toUpperCase(), labelW - 10);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const valueLines = doc.splitTextToSize(value || '', contentWidth - labelW - 10);
    const height = Math.max(minHeight, Math.max(labelLines.length, valueLines.length) * 11 + 8);

    doc.setDrawColor(BORDER);
    doc.setLineWidth(0.5);
    doc.setFillColor(LABEL_BG);
    doc.rect(MARGIN, y, labelW, height, 'F');
    doc.rect(MARGIN, y, labelW, height, 'S');
    doc.rect(MARGIN + labelW, y, contentWidth - labelW, height, 'S');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor('#555f68');
    doc.text(labelLines, MARGIN + 5, y + 11);
    this.addTextField(doc, value || '', MARGIN + labelW + 3, y + 2, contentWidth - labelW - 6, height - 4, true);
    return y + height;
  }

  /** Adds a real, fillable AcroForm text field over a value cell — pre-filled when `value` is given, blank and still editable otherwise. */
  private addTextField(doc: jsPDF, value: string, x: number, y: number, width: number, height: number, multiline = false): void {
    const tf = new (jsPDF as any).AcroForm.TextField();
    tf.fieldName = this.nextFieldName('field');
    tf.value = value || '';
    tf.x = x;
    tf.y = y;
    tf.width = Math.max(width, 4);
    tf.height = Math.max(height, 4);
    tf.fontSize = 9;
    tf.fontName = 'helvetica';
    tf.color = TEXT;
    if (multiline) tf.multiline = true;
    doc.addField(tf);
  }

  /**
   * jsPDF's own "checked" appearance draws only its caption glyph (ZapfDingbats "3") with no border
   * at all, inconsistent with — and much less legible than — the clean bordered square PDF viewers
   * natively draw for the unchecked state. Overriding it with a plain vector box + checkmark (built
   * on the same XObject `jsPDF.AcroForm.Appearance.CheckBox.YesNormal` returns, just with our own
   * drawing commands in its `.stream`) renders identically in every viewer regardless of font
   * availability, and actually looks like a checkbox.
   */
  private static checkedCheckboxAppearance(formObject: any): any {
    const xobj = (jsPDF as any).AcroForm.Appearance.CheckBox.YesNormal(formObject);
    const w = Number(xobj.BBox[2]) || 10;
    const h = Number(xobj.BBox[3]) || 10;
    const lineWidth = Math.max(0.9, w * 0.14);
    xobj.stream = [
      '1 g',
      `0 0 ${w.toFixed(2)} ${h.toFixed(2)} re`,
      'f',
      '0 G',
      '0.75 w',
      `0.4 0.4 ${(w - 0.8).toFixed(2)} ${(h - 0.8).toFixed(2)} re`,
      'S',
      '0 G',
      `${lineWidth.toFixed(2)} w`,
      '1 J 1 j',
      `${(w * 0.2).toFixed(2)} ${(h * 0.52).toFixed(2)} m`,
      `${(w * 0.42).toFixed(2)} ${(h * 0.26).toFixed(2)} l`,
      `${(w * 0.82).toFixed(2)} ${(h * 0.76).toFixed(2)} l`,
      'S',
    ].join('\n');
    return xobj;
  }

  /** Adds a real, tickable AcroForm checkbox at the given point, pre-ticked when `checked`. */
  private addCheckBox(doc: jsPDF, x: number, y: number, size: number, checked: boolean): void {
    const cb = new (jsPDF as any).AcroForm.CheckBox();
    cb.fieldName = this.nextFieldName('cb');
    cb.x = x;
    cb.y = y;
    cb.width = size;
    cb.height = size;
    cb.appearanceState = checked ? 'On' : 'Off';
    cb.appearanceStreamContent = {
      N: { On: ExternalEoiPdfService.checkedCheckboxAppearance },
      D: { On: ExternalEoiPdfService.checkedCheckboxAppearance, Off: (jsPDF as any).AcroForm.Appearance.CheckBox.OffPushDown },
    };
    doc.addField(cb);
  }

  private nextFieldName(prefix: string): string {
    this.fieldSeq += 1;
    return `${prefix}_${this.fieldSeq}`;
  }

  /** A real checkbox field plus its (possibly wrapped) static text label beside it. */
  private drawCheckboxLines(doc: jsPDF, x: number, y: number, checked: boolean, lines: string[], fontSize = 8): void {
    const size = CHECKBOX_SIZE;
    this.addCheckBox(doc, x, y - size + 2, size, checked);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(fontSize);
    doc.setTextColor(TEXT);
    doc.text(lines, x + size + 4, y);
  }

  /**
   * A label row whose value cell holds one or more checkbox options, one per line — each option's
   * text wraps within the value column rather than running off the page edge (needed for the credit
   * card surcharge acknowledgement, the longest option on the form).
   */
  private drawCheckboxRow(
    doc: jsPDF,
    label: string,
    options: string[],
    checkedValues: string[],
    y: number,
    pageWidth: number,
    lineHeight = 13
  ): number {
    const contentWidth = pageWidth - MARGIN * 2;
    const labelW = contentWidth * LABEL_COL_RATIO;
    const optionIndent = 6 + CHECKBOX_SIZE + 4;
    const maxTextWidth = contentWidth - labelW - optionIndent - 6;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    const optionLines = options.map(opt => doc.splitTextToSize(opt, maxTextWidth) as string[]);

    doc.setFontSize(7.5);
    const labelLines = label ? doc.splitTextToSize(label.toUpperCase(), labelW - 10) : [];

    const totalOptionLines = optionLines.reduce((sum, lines) => sum + lines.length, 0);
    const height = Math.max(18, totalOptionLines * lineHeight + 6, labelLines.length * 10 + 8);

    doc.setDrawColor(BORDER);
    doc.setLineWidth(0.5);
    doc.setFillColor(LABEL_BG);
    doc.rect(MARGIN, y, labelW, height, 'F');
    doc.rect(MARGIN, y, labelW, height, 'S');
    doc.rect(MARGIN + labelW, y, contentWidth - labelW, height, 'S');
    if (label) {
      doc.setTextColor('#555f68');
      doc.text(labelLines, MARGIN + 5, y + 11);
    }
    let cy = y + 11;
    options.forEach((opt, i) => {
      const lines = optionLines[i];
      this.drawCheckboxLines(doc, MARGIN + labelW + 6, cy, checkedValues.includes(opt), lines);
      cy += lines.length * lineHeight;
    });
    return y + height;
  }

  private drawFooter(doc: jsPDF, data: ExternalEoiRetailPdfData, pageWidth: number, pageHeight: number): void {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7.5);
    doc.setTextColor('#6b7680');
    doc.text(`${data.projectName || DEFAULT_PROJECT_NAME} – Retail Expression of Interest`, pageWidth / 2, pageHeight - 20, { align: 'center' });
  }
}
